export type TrackingType =
    | 'google-tag'        // Google Tag Manager
    | 'google-analytics'  // Google Analytics 4
    | 'google-ads'        // Google Ads conversion tracking
    | 'amp-analytics'     // AMP Analytics
    | 'facebook-pixel';   // Facebook Pixel

/**
 * Supported conversion event types
 */
export type ConversionEventType =
    | 'purchase'          // Purchase/conversion event
    | 'lead'              // Lead generation
    | 'signup'            // User signup
    | 'page_view';        // Page view conversion

/**
 * Tracking configuration interface
 */
export interface TrackingConfig {
    type: TrackingType;
    id?: string; // ID is required for most tracking types except amp-analytics
}

/**
 * Conversion event configuration interface
 */
export interface ConversionEventConfig {
    type: ConversionEventType;
    conversionLabel?: string; // Google Ads conversion label
    value?: number; // Conversion value
    currency?: string; // Currency code
}

/**
 * Tenant tracking configuration interface
 */
export interface TenantTrackingConfig {
    tenantId: number;
    storeId?: number; // Optional store-level specificity
    tracking: TrackingConfig[];
    conversionEvents?: ConversionEventConfig[]; // Optional conversion events
}

/**
 * Tracking configurations for different tenants
 * 
 * To add tracking for a new tenant, simply add a new entry to this array:
 * 
 * ```typescript
 * {
 *     tenantId: NEW_TENANT_ID,
 *     tracking: [
 *         {
 *             type: 'google-analytics',
 *             id: 'GA-XXXXXXXX'
 *         },
 *         {
 *             type: 'facebook-pixel',
 *             id: 'XXXXXXXXXXXXXXX'
 *         }
 *     ]
 * }
 * ```
 * 
 * Available tracking types:
 * - `google-tag`: Google Tag Manager (requires ID)
 * - `google-analytics`: Google Analytics 4 (requires ID)
 * - `google-ads`: Google Ads conversion tracking (requires ID)
 * - `amp-analytics`: AMP Analytics (no ID required)
 * - `facebook-pixel`: Facebook Pixel (requires ID)
 */
export const TRACKING_CONFIGS: TenantTrackingConfig[] = [
    {
        tenantId: 286,//LOOK_WOW
        tracking: [
            { type: 'google-tag', id: 'AW-16565257530' },
            { type: 'amp-analytics' }
        ],
        conversionEvents: [
            { type: 'purchase', conversionLabel: 'YiqyCJCv_bAZELqK99o9' }
        ]
    },
    {
        tenantId: 554,//ZIRA_STUDIO_SALON
        tracking: [
            { type: 'google-analytics', id: 'G-W4YQB43V7C' },
            { type: 'google-ads', id: 'AW-16886929050' },
            { type: 'amp-analytics' }
        ]
    },
    {
        tenantId: 625,//JAWED_HABIB_HAIR_BEAUTY_SALON_KURNOOL
        storeId: 752,
        tracking: [
            { type: 'facebook-pixel', id: '716407874150684' }
        ]
    },
    {
        tenantId: 581,//KERIAN_SALON_DELHI
        tracking: [
            { type: 'google-tag', id: 'AW-17756018442' },
            { type: 'amp-analytics' }
        ]
    }
];

/**
 * Helper function to get tracking configuration for a specific tenant
 * 
 * @param tenantId - The tenant ID to search for
 * @param storeId - Optional store ID for store-specific tracking
 * @returns The tracking configuration for the tenant, or null if not found
 */
export function getTrackingConfig(tenantId: number, storeId?: number): TenantTrackingConfig | null {
    return TRACKING_CONFIGS.find(config => {
        const tenantMatches = config.tenantId === tenantId;
        const storeMatches = !config.storeId || config.storeId === storeId;
        return tenantMatches && storeMatches;
    }) || null;
}
